import { useCallback, useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/SidePanel";
import {
  ScatterChart, Scatter, XAxis, YAxis, ZAxis, ReferenceLine, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import { Target, Loader2, CheckCircle2, XCircle, Gauge } from "lucide-react";
import { api } from "@/lib/api";
import { HAZARD_LABEL, HAZARD_BASE } from "@/lib/hazard";
import { ScoringClock } from "@/components/ScoringClock";
import { AlertPolicy, OutcomeBar, OUTCOME } from "@/components/AlertPolicy";
import { ConfidenceHistogram, NearMisses } from "@/components/TuningViews";

const pct = (v) => (v == null ? "—" : `${Math.round(v * 100)}%`);

const MATURITY = {
  prior: { label: "Prior (no outcomes yet)", color: "text-atlas-secondary" },
  calibrating: { label: "Calibrating on live outcomes", color: "text-atlas-orange" },
  mature: { label: "Mature — data-driven", color: "text-emerald-400" },
};

// Brier: lower is better. 0 = perfect, 0.25 = coin-flip baseline.
const brierTone = (b) =>
  b == null ? "text-atlas-secondary" : b <= 0.15 ? "text-emerald-400" : b <= 0.25 ? "text-atlas-cyan" : "text-atlas-orange";

const Big = ({ label, value, tone, testid }) => (
  <div className="flex flex-col px-3 py-2 border border-white/10 bg-atlas-void/60 flex-1" data-testid={testid}>
    <span className="text-[9px] uppercase tracking-widest text-atlas-cyan/80 font-mono">{label}</span>
    <span className={`text-2xl font-mono leading-tight ${tone || "text-white"}`}>{value}</span>
  </div>
);

const ReliabilityDot = ({ cx, cy, payload }) => {
  const color = HAZARD_BASE[payload.hazard] || "#00F0FF";
  const r = Math.min(9, 3 + Math.sqrt(payload.n));
  return <circle cx={cx} cy={cy} r={r} fill={color} fillOpacity={0.85} stroke="#0D1117" strokeWidth={1} />;
};

const ReliabilityTip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-atlas-void border border-white/15 px-2 py-1.5 text-[10px] font-mono">
      <div className="text-white">{HAZARD_LABEL[p.hazard] || p.hazard} · {p.bucket}</div>
      <div className="text-atlas-cyan">predicted {pct(p.x / 100)}</div>
      <div className="text-emerald-400">observed {pct(p.y / 100)}</div>
      <div className="text-atlas-secondary/85">{p.n} outcomes</div>
    </div>
  );
};

export const CalibrationPanel = ({ open, onOpenChange }) => {
  const [report, setReport] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, p] = await Promise.all([api.calibration(), api.predictions("resolved", 40)]);
      setReport(r);
      setReceipts(p);
    } catch (e) {
      /* best-effort */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [open, load]);

  const overall = report?.overall || {};
  const hazards = report?.hazards || [];
  const scatter = [];
  hazards.forEach((h) =>
    h.points.forEach((pt) => {
      if (pt.observed != null && pt.n > 0)
        scatter.push({ x: pt.predicted * 100, y: pt.observed * 100, n: pt.n, hazard: h.hazard, bucket: pt.bucket });
    })
  );
  const maturity = MATURITY[overall.maturity] || MATURITY.prior;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        aria-describedby={undefined}
        className="w-full sm:max-w-lg bg-atlas-surface border-l border-white/10 p-0 text-atlas-foreground"
        data-testid="calibration-panel"
      >
        <SheetHeader className="h-14 px-4 flex flex-row items-center justify-between border-b border-white/10 space-y-0">
          <SheetTitle className="flex items-center gap-2 text-white font-display">
            <Target className="w-4 h-4 text-atlas-cyan" />
            Calibration &amp; Track Record
          </SheetTitle>
          {loading && <Loader2 className="w-4 h-4 text-atlas-cyan animate-spin" />}
        </SheetHeader>

        <div className="h-[calc(100vh-3.5rem)] overflow-y-auto p-4">
          <p className="text-[11px] text-atlas-secondary leading-relaxed mb-3">
            Vigilo Earth scores its own calls. Every hypothesis is logged, then checked against what actually
            happened. The percentages below are <span className="text-atlas-cyan">Bayesian-calibrated</span> —
            posterior hit-rates from real outcomes, not the AI's self-rating.
          </p>

          <ScoringClock open={open} />

          <div className="flex gap-2 mb-4" data-testid="calibration-overview">
            <Big label="Resolved calls" value={overall.resolved ?? 0} testid="calib-resolved" />
            <Big label="Hit rate" value={pct(overall.hit_rate)} tone="text-emerald-400" testid="calib-hitrate" />
            <Big
              label={overall.reliable ? "Brier score" : `Brier · needs ${overall.min_n || 20} scored`}
              value={overall.brier == null ? "—" : overall.brier.toFixed(3)}
              tone={brierTone(overall.brier)}
              testid="calib-brier"
            />
            <Big
              label="Brier skill"
              value={overall.brier_skill == null || !overall.reliable ? "—" : (overall.brier_skill > 0 ? "+" : "") + overall.brier_skill.toFixed(2)}
              tone={overall.brier_skill > 0 ? "text-emerald-400" : "text-atlas-secondary"}
              testid="calib-brier-skill"
            />
          </div>

          <OutcomeBar oc={overall.outcomes} />
          <ConfidenceHistogram open={open} />
          <AlertPolicy />
          <NearMisses open={open} />

          <div className="flex items-center gap-1.5 mb-4 text-[10px] font-mono">
            <Gauge className={`w-3.5 h-3.5 ${maturity.color}`} />
            <span className="uppercase tracking-widest text-atlas-secondary/80">Status</span>
            <span className={maturity.color}>{maturity.label}</span>
          </div>

          {/* Reliability diagram */}
          <div className="mb-5">
            <div className="text-[10px] uppercase tracking-widest text-atlas-cyan/60 font-mono mb-1.5">
              Reliability Diagram — predicted vs. actual
            </div>
            <div className="h-52 w-full border border-white/10 bg-atlas-void/60 p-1" data-testid="reliability-diagram">
              <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 112 }}>
                <ScatterChart margin={{ top: 8, right: 12, bottom: 16, left: -8 }}>
                  <XAxis
                    type="number" dataKey="x" domain={[0, 100]} tickCount={6}
                    tick={{ fill: "#8B98A5", fontSize: 8 }} unit="%"
                    label={{ value: "predicted", position: "insideBottom", offset: -6, fill: "#8B98A5", fontSize: 8 }}
                  />
                  <YAxis
                    type="number" dataKey="y" domain={[0, 100]} tickCount={6}
                    tick={{ fill: "#8B98A5", fontSize: 8 }} unit="%" width={34}
                    label={{ value: "actual", angle: -90, position: "insideLeft", offset: 16, fill: "#8B98A5", fontSize: 8 }}
                  />
                  <ZAxis dataKey="n" range={[40, 300]} />
                  <ReferenceLine
                    segment={[{ x: 0, y: 0 }, { x: 100, y: 100 }]}
                    stroke="#8B98A5" strokeDasharray="4 4" strokeOpacity={0.5} ifOverflow="extendDomain"
                  />
                  <Tooltip content={<ReliabilityTip />} cursor={{ strokeOpacity: 0.1 }} />
                  <Scatter data={scatter} shape={<ReliabilityDot />} isAnimationActive={false}>
                    {scatter.map((s, i) => (
                      <Cell key={i} />
                    ))}
                  </Scatter>
                </ScatterChart>
              </ResponsiveContainer>
            </div>
            <div className="text-[9px] font-mono text-atlas-secondary/75 mt-1">
              Dashed line = perfect calibration. Dots on it = Vigilo Earth's stated confidence matches reality. Dot size = sample count.
            </div>
          </div>

          {/* Per-hazard track record */}
          <div className="mb-5">
            <div className="text-[10px] uppercase tracking-widest text-atlas-cyan/60 font-mono mb-1.5">
              Track record by hazard
            </div>
            <div className="space-y-1.5" data-testid="hazard-track-record">
              {hazards.map((h) => (
                <div
                  key={h.hazard}
                  className="flex items-center gap-2 px-2.5 py-2 border border-white/10 bg-atlas-void/40"
                  data-testid={`track-${h.hazard}`}
                >
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: HAZARD_BASE[h.hazard] }} />
                  <span className="text-[11px] font-mono text-white w-20 shrink-0">
                    {HAZARD_LABEL[h.hazard] || h.hazard}
                  </span>
                  <div className="flex gap-1 flex-1">
                    {h.points.map((pt) => (
                      <div key={pt.bucket} className="flex-1 text-center">
                        <div className="text-[8px] font-mono uppercase text-atlas-secondary/75">{pt.bucket}</div>
                        <div className="text-[11px] font-mono text-atlas-cyan">{pct(pt.predicted)}</div>
                        <div className="text-[8px] font-mono text-atlas-secondary/75">
                          {pt.observed != null ? `${pct(pt.observed)}·${pt.n}` : "prior"}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="text-right w-14 shrink-0">
                    <div className="text-[8px] font-mono uppercase text-atlas-secondary/75">brier</div>
                    <div className={`text-[11px] font-mono ${brierTone(h.brier)}`}>
                      {h.brier == null ? "—" : h.brier.toFixed(3)}
                    </div>
                  </div>
                </div>
              ))}
              {hazards.length === 0 && (
                <div className="text-[11px] font-mono text-atlas-secondary/75 py-4 text-center">
                  No calibration data yet — the engine seeds priors then learns from real outcomes.
                </div>
              )}
            </div>
          </div>

          {/* Receipts */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-atlas-cyan/60 font-mono mb-1.5">
              Recent resolved calls (receipts)
            </div>
            <div className="space-y-1" data-testid="calibration-receipts">
              {receipts.map((r) => {
                const hit = r.outcome === "hit";
                const oc = (r.late ? OUTCOME.late : OUTCOME[r.outcome]) || OUTCOME.miss;
                const sig = r.signal_at_scoring;
                return (
                  <div
                    key={r.uid}
                    className="flex items-start gap-2 px-2.5 py-1.5 border-b border-white/5"
                    data-testid={`receipt-${r.uid}`}
                  >
                    {hit ? (
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />
                    ) : (
                      <XCircle className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${oc.text}`} />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[9px] font-mono uppercase" style={{ color: HAZARD_BASE[r.hazard] }}>
                          {HAZARD_LABEL[r.hazard] || r.hazard}
                        </span>
                        <span className="text-[10px] font-mono text-white/90 truncate">{r.region}</span>
                        {r.source === "backtest" && (
                          <span className="text-[7px] font-mono uppercase tracking-widest text-atlas-cyan/80 border border-atlas-cyan/30 px-1">
                            backtest
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                        <span className={`text-[8px] font-mono uppercase tracking-widest border px-1 ${oc.text} ${oc.border}`} data-testid={`receipt-outcome-${r.uid}`}>{oc.label}</span>
                        {r.outcome === "persisted" && sig && (
                          <span className="text-[9px] font-mono text-amber-300/80" data-testid={`receipt-signal-${r.uid}`}>
                            signal at scoring: {sig.value ?? "—"}{sig.unit ? ` ${sig.unit}` : ""} · {sig.sigma}σ
                          </span>
                        )}
                        {r.sar_verified && (
                          <span className="text-[8px] font-mono uppercase tracking-widest border px-1 text-violet-300 border-violet-400/40">Satellite-verified (Vigilo Earth SAR analysis)</span>
                        )}
                        {r.confirmed_by?.basin && <span className="text-[9px] font-mono text-atlas-secondary">{r.confirmed_by.basin}</span>}
                      </div>
                      <div className="text-[10px] text-atlas-secondary leading-snug">{r.outcome_note || r.title}</div>
                    </div>
                    <span
                      className={`text-[10px] font-mono shrink-0 ${hit ? "text-emerald-400" : "text-atlas-secondary/75"}`}
                    >
                      {pct(r.calibrated_score)}
                    </span>
                  </div>
                );
              })}
              {receipts.length === 0 && (
                <div className="text-[11px] font-mono text-atlas-secondary/75 py-4 text-center">
                  No resolved calls yet. As horizons elapse, verified outcomes appear here.
                </div>
              )}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
